import { Directive, effect, ElementRef, inject, input, Renderer2 } from '@angular/core';

@Directive({
  selector: '[commonUtilitiesTrackableId]',
  standalone: true,
})
export class TrackableIdDirective {
  // Injections
  el = inject(ElementRef);
  renderer = inject(Renderer2);

  // Inputs
  trackerId = input.required<string>();
  isSharedComponent = input<boolean>(false);

  constructor() {
    effect(() => {
      if (this.trackerId()) {
        if (this.isNameUniqueInParent()) {
          const uniqueId = this.generateUniqueId();
          this.renderer.setAttribute(this.el.nativeElement, 'data-tracker', uniqueId);
        } else {
          this.warnDeveloper();
        }
      }
    });
  }

  private generateUniqueId(): string {
    const parentTagName = this.tagNameShortener(this.getParentComponentTagName());
    const tagName = this.tagNameShortener(this.el.nativeElement.tagName.toLowerCase());
    return `${parentTagName}-${tagName}-${this.trackerId().toLowerCase()}`;
  }

  private getParentComponentTagName(): string {
    let parentElement = this.el.nativeElement.parentElement;
    let foundFirstComponent = false;

    while (parentElement) {
      const tagName = parentElement.tagName.toLowerCase();
      if (!['div', 'span', 'p', 'section', 'header', 'footer', 'main', 'article', 'aside', 'nav'].includes(tagName)) {
        // If isSharedComponent is true, skip the first component found
        if (this.isSharedComponent() && !foundFirstComponent) {
          foundFirstComponent = true;
        } else {
          return tagName;
        }
      }
      parentElement = parentElement.parentElement;
    }
    return 'unknown-component';
  }

  private tagNameShortener(tagName: string): string {
    const tagNameSections = tagName.split('-');
    if (tagNameSections.length < 3) {
      return tagName;
    }
    const shortened = tagNameSections
      .slice(0, -2)
      .map((section) => section.charAt(0))
      .join('');
    return `${shortened}-${tagNameSections.slice(-2).join('-')}`;
  }

  // Checks if the name is unique within the parent's children.
  private isNameUniqueInParent(): boolean {
    const parent = this.el.nativeElement.parentNode;
    const siblings = Array.from(parent.children) as Element[];
    const nameMatches = siblings.filter(
      (element: Element) => element !== this.el.nativeElement && element.getAttribute('trackerId') === this.trackerId(),
    );
    return nameMatches.length === 0;
  }

  private warnDeveloper() {
    console.error(`trackerId Error: The name "${this.trackerId()}" is not unique within its parent element's children.`);
  }
}
